import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Heart, HandHeart, Share2, X } from "lucide-react";
import { toast } from "sonner";

const SEEN_KEY = "dh_welcome_seen";

// Don't cover dense detail/reader pages (paths with a slug, e.g. /deities/shiva).
const isDetailPage = (path) => path.split("/").filter(Boolean).length >= 2;

export default function WelcomeNote() {
  const { pick } = useLang();
  const { pathname } = useLocation();
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    if (isDetailPage(pathname)) return;
    setMounted(true);
    const t = setTimeout(() => setShow(true), 1400);
    return () => clearTimeout(t);
  }, [pathname]);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(SEEN_KEY, "1");
    setTimeout(() => setMounted(false), 400);
  };

  const share = async () => {
    const data = {
      title: "Dharmartha",
      text: pick("धर्मार्थ — सनातन धर्म का डिजिटल ज्ञान-कोश।", "Dharmartha — a digital knowledge resource for Sanatana Dharma."),
      url: window.location.origin,
    };
    try {
      if (navigator.share) await navigator.share(data);
      else {
        await navigator.clipboard.writeText(data.url);
        toast(pick("लिंक कॉपी हो गया", "Link copied"), { description: pick("धर्मार्थ को साझा करें", "Share Dharmartha with others") });
      }
    } catch { /* user cancelled */ }
  };

  if (!mounted) return null;

  return (
    <div
      data-testid="welcome-note"
      className={`fixed inset-x-4 bottom-4 z-40 sm:left-6 sm:right-auto sm:max-w-sm transition-all duration-500 ease-out ${
        show ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      }`}
    >
      <div className="relative border border-line bg-surface shadow-[0_12px_40px_rgba(28,22,16,0.16)]">
        <button
          onClick={dismiss}
          data-testid="welcome-dismiss"
          aria-label="Dismiss"
          className="absolute right-3 top-3 text-ink-3 transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] font-deva text-xl text-saffron">ॐ</span>
            <p className="font-serif-latin text-lg text-ink">{pick("हमने अभी आरंभ किया है", "We're just getting started")}</p>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            {pick(
              "धर्मार्थ एक स्वप्रेरित सामुदायिक पहल है — हम इसे एक-एक पृष्ठ, परंपरा एवं संसाधन के साथ बना रहे हैं। प्रतिदिन नई सामग्री, मंदिर, प्रथाएँ एवं स्रोत जोड़े जा रहे हैं।",
              "Dharmartha is a bootstrapped community initiative — we're building it one page, one tradition and one resource at a time. New content, temples, practices and sources are added every day."
            )}
          </p>
          <p className="mt-3 text-xs text-ink-3">
            {pick("यदि यह उपयोगी लगे तो — सहयोग करें, योगदान दें, साझा करें। 🙏", "If you find it useful — support, contribute and share. 🙏")}
          </p>
          <div className="mt-5 flex items-center gap-2">
            <Link to="/support" onClick={dismiss} data-testid="welcome-support" className="inline-flex flex-1 items-center justify-center gap-1.5 bg-[var(--saffron)] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
              <Heart className="h-3.5 w-3.5" /> {pick("सहयोग", "Support")}
            </Link>
            <Link to="/feedback" onClick={dismiss} data-testid="welcome-contribute" className="inline-flex flex-1 items-center justify-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
              <HandHeart className="h-3.5 w-3.5" /> {pick("योगदान", "Contribute")}
            </Link>
            <button onClick={share} data-testid="welcome-share" className="inline-flex flex-1 items-center justify-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
              <Share2 className="h-3.5 w-3.5" /> {pick("साझा", "Share")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
